import { BreakpointValue } from 'consts/breakpoints';
import styled from '@emotion/styled';
import { get } from 'lodash';
import { SmallText } from 'ds/components/typography';
import { colorToAlpha } from 'helpers/insight';
import { rangeToGalleryHeight } from '../config';

export const BORDER_PADDING_FIX = 1;
export const MAX_IMAGES_LAYOUT_COUNT = 5;

export function getLayoutCssProps(totalImagesCount: number, idx: number, br: string): React.CSSProperties {
  const layout: Record<number, React.CSSProperties[]> = {
    1: [ { gridColumnStart: 1, gridColumnEnd: 5, gridRowStart: 1, gridRowEnd: 3, borderRadius: br } ],
    2: [
      { gridColumnStart: 1, gridColumnEnd: 3, gridRowStart: 1, gridRowEnd: 3, borderTopRightRadius: br, borderBottomRightRadius: br },
      { gridColumnStart: 3, gridColumnEnd: 5, gridRowStart: 1, gridRowEnd: 3, borderTopLeftRadius: br, borderBottomLeftRadius: br },
    ],
    3: [
      { gridColumnStart: 1, gridColumnEnd: 3, gridRowStart: 1, gridRowEnd: 3, borderTopRightRadius: br, borderBottomRightRadius: br },
      { gridColumnStart: 3, gridColumnEnd: 5, gridRowStart: 1, gridRowEnd: 2, borderTopLeftRadius: br },
      { gridColumnStart: 3, gridColumnEnd: 5, gridRowStart: 2, gridRowEnd: 3, borderBottomLeftRadius: br },
    ],
    4: [
      { gridColumnStart: 1, gridColumnEnd: 3, gridRowStart: 1, gridRowEnd: 3, borderTopRightRadius: br, borderBottomRightRadius: br },
      { gridColumnStart: 3, gridColumnEnd: 5, gridRowStart: 1, gridRowEnd: 2, borderTopLeftRadius: br },
      { gridColumnStart: 3, gridColumnEnd: 4, gridRowStart: 2, gridRowEnd: 3 },
      { gridColumnStart: 4, gridColumnEnd: 5, gridRowStart: 2, gridRowEnd: 3, borderBottomLeftRadius: br },
    ],
    [MAX_IMAGES_LAYOUT_COUNT]: [
      { gridColumnStart: 1, gridColumnEnd: 3, gridRowStart: 1, gridRowEnd: 3, borderTopRightRadius: br, borderBottomRightRadius: br },
      { gridColumnStart: 3, gridColumnEnd: 4, gridRowStart: 1, gridRowEnd: 2 },
      { gridColumnStart: 4, gridColumnEnd: 5, gridRowStart: 1, gridRowEnd: 2, borderTopLeftRadius: br },
      { gridColumnStart: 3, gridColumnEnd: 4, gridRowStart: 2, gridRowEnd: 3 },
      { gridColumnStart: 4, gridColumnEnd: 5, gridRowStart: 2, gridRowEnd: 3, borderBottomLeftRadius: br },
    ],
  };

  const layoutImagesCount = totalImagesCount > MAX_IMAGES_LAYOUT_COUNT ? MAX_IMAGES_LAYOUT_COUNT : totalImagesCount;
  return get(layout, [ layoutImagesCount, idx ]);
}

export const ImageGrid = styled.div`
  display: grid;
  grid-gap: ${({ theme }) => theme.spacing(1)};
  grid-template-columns: repeat(4, 1fr);

  ${({ theme }) => Object.keys(rangeToGalleryHeight).map(key => (
    theme.media.showAt({ at: (+key as BreakpointValue) })`
      grid-template-rows: repeat(2, ${rangeToGalleryHeight[key] / 2}px);
    `
  ))}
`;

interface IPlaceholderImage {
  imageUrl: string;
}

export const PlaceholderImage = styled.div<IPlaceholderImage>`
  background-image: ${({ imageUrl }) => `url(${imageUrl})`};
  background-color: rgb(255, 255, 255);
  background-size: cover;
  background-repeat: no-repeat;
  background-position: bottom center;
  border-radius: ${({ theme }) => theme.borderRadius.small};

  ${({ theme }) => Object.keys(rangeToGalleryHeight).map(key => (
    theme.media.showAt({ at: (+key as BreakpointValue) })`
      height: ${rangeToGalleryHeight[key]}px;
    `
  ))}
`;

export const Root = styled.div`
  position: relative;
`;

const CommonControls = styled.div`
  ${({ theme }) => `
    position: absolute;
    display: flex;
    gap: ${theme.spacing(2)};
  `}
`;

export const TopLeftControls = styled(CommonControls)`
  ${({ theme }) => `
    left: ${theme.spacing(2)};
    top: ${theme.spacing(2)};
  `}
`;

export const TopRightControls = styled(CommonControls)`
  ${({ theme }) => `
    right: ${theme.spacing(2)};
    top: ${theme.spacing(2)};
  `}
`;

export const BottomLeftControls = styled(CommonControls)`
  ${({ theme }) => `
    left: ${theme.spacing(2)};
    bottom: ${theme.spacing(2)};
  `}
`;

export const BottomRightControls = styled(CommonControls)`
  ${({ theme }) => `
    right: ${theme.spacing(2)};
    bottom: ${theme.spacing(2)};
  `}
`;

export const ImageGridFullScreen = styled.div();

export const MiddleLegend = styled.div(({ theme }) => ({
  borderRadius: theme.borderRadius.small,
  padding: theme.spacing(0.5, 1.5),
  color: theme.colors.neutrals.white,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  position: 'relative',
  left: '-50%',
  zIndex: 1,
  transform: 'translateX(50%)',
  backgroundColor: colorToAlpha(theme.colors.neutrals.black, 0.5),
}));

export const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const ImageRoot = styled.div`
  cursor: pointer;
  overflow: hidden;
  position: relative;
  > img {
    transition: transform 300ms ease;
  }
  &:hover > img {
    transform: scale(1.02);
  }
`;

export const AllImageButton = styled(SmallText)`
  ${({ theme }) => `
    cursor: pointer;
    color: ${theme.colors.neutrals.grey1};
    padding: ${theme.spacing(0.75, 1.5)};
    display: flex;
    gap: ${theme.spacing(0.5)};
    align-items: center;
    border: 1px solid ${theme.colors.neutrals.grey6};
    border-radius: ${theme.borderRadius.small};
    background-color: ${theme.colors.neutrals.white};
    transition: background-color 300ms ease;
    :hover {
      background-color: ${theme.colors.neutrals.grey7};
    }
  `}
`;

